import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getTemplateOptions } from '../../../../store/features/automationsSlice';
import DropDown from '../../../McDropdown';

const RenderInput = ({ config, dependentConfig = null, errorFields, updateIntegrationParams }) => {
  const inputRef = useRef(null);
  const [cursor, setCursor] = useState(null);

  const updateInputHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = '0px';
      const scrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = scrollHeight + 'px';
      return scrollHeight + 'px';
    }
  };

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input) {
      input.setSelectionRange(cursor, cursor);
      updateInputHeight();
    }
  }, [inputRef, cursor]);

  return config.conditional ? (
    <>
      {config.conditional.param_value === dependentConfig?.value && (
        <div className="flex integration-input relative w-full">
          <textarea
            ref={inputRef}
            autoComplete="turnoff"
            className={`input name-input-form w-full ${errorFields[config.id] && 'error-info'}`}
            onChange={e => {
              setCursor(e.target.selectionStart);
              updateIntegrationParams(config.id, e.target.value);
            }}
            placeholder={config.name}
            type="text"
            value={config.value || ''}
          />
        </div>
      )}
    </>
  ) : config.param_type === 'LIST' ? (
    <div className="flex-column">
      <DropDown
        className={`integration-input-dropdown ${!errorFields[config.id] ? 'error-info' : ''}`}
        setSelected={option => updateIntegrationParams(config.id, option.id, option)}
        options={config.choices}
        placeholder={config.name}
        selected={config.choices?.find(type => type.id === config.value)}
        withIcon={false}
      />
    </div>
  ) : (
    <div className="flex integration-input relative w-full">
      <textarea
        ref={inputRef}
        autoComplete="turnoff"
        className={`input name-input-form w-full ${errorFields[config.id] && 'error-info'}`}
        onChange={e => {
          setCursor(e.target.selectionStart);
          updateIntegrationParams(config.id, e.target.value);
        }}
        placeholder={config.name}
        type="text"
        defaultValue={config.value || ''}
      />
    </div>
  );
};

const RenderConditionalConfigs = ({
  config,
  dependentConfig = null,
  parentConfig = null,
  errorFields,
  updateIntegrationParams,
}) => {
  return (
    <>
      {(!config.conditional || (config.conditional && config.conditional.param_value === dependentConfig?.value)) && (
        <div className="flex-column action-container relative">
          <div className="flex-column mb-6">
            <label className="regular-text font-12 grey-text mb-1 pxy-1">{config.name}</label>
            <RenderInput
              key={config?.id}
              config={config}
              dependentConfig={dependentConfig}
              errorFields={errorFields}
              updateIntegrationParams={updateIntegrationParams}
            />
          </div>
          {config.depends?.map(c => (
            <RenderConditionalConfigs
              key={c.id}
              config={c}
              dependentConfig={config}
              parentConfig={dependentConfig ? parentConfig : config}
              errorFields={errorFields}
              updateIntegrationParams={updateIntegrationParams}
            />
          ))}
        </div>
      )}
    </>
  );
};

const AutomationLogIntegrationDetails = props => {
  const {
    automationLogIntegrationDetails,
    sendDataToIntegration,
    selectActions,
    selectedIntegration,
    editAutomationActionType,
    editAutomationActionData,
  } = props;

  const getInitialConfigDetailValues = () => {
    if (editAutomationActionType === 'ACTION_DATA') {
      const params = editAutomationActionData?.params || [];
      return params.map(param => ({
        param_name: param.param_type,
        param_value: param.param_value,
        display_name: param.display_value,
      }));
    } else {
      return [];
    }
  };

  const [errorFields, setErrorFields] = useState({});
  const [configDetailValues, setConfigDetailValues] = useState(getInitialConfigDetailValues());
  const [configDetails, setConfigDetails] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const getConditional = config => {
    const findCondition = automationLogIntegrationDetails.filter(c => c.conditional?.param_name === config.id);
    const finalData = findCondition.map(f => ({
      ...f,
      value: getIntegrationParamValue(f.id),
      depends: getConditional(f),
    }));
    return finalData;
  };

  const getIntegrationParamValue = name => {
    return configDetailValues?.find(param => param.param_name === name)?.param_value || '';
  };

  const updateIntegrationParams = (name, value, displayName) => {
    const isAvailable = configDetailValues?.find(param => param.param_name === name);
    if (isAvailable) {
      setConfigDetailValues([
        ...configDetailValues.map(param =>
          param.param_name === name ? { ...param, param_value: value, display_name: displayName?.name } : param,
        ),
      ]);
    } else {
      setConfigDetailValues([
        ...configDetailValues,
        { param_name: name, param_value: value, display_name: displayName?.name },
      ]);
    }
    setErrorFields({ ...errorFields, [name]: false });
  };

  useEffect(() => {
    const fetchData = async () => {
      const configs = await Promise.all(
        automationLogIntegrationDetails?.map(async curr => {
          if (!curr.conditional) {
            if (curr?.param_type === 'LIST') {
              const filteredConfigs = configDetails.filter(item => item.param_type === 'LIST');
              const matchingConfig = filteredConfigs.find(config => config.id === curr.id);
              if (matchingConfig?.choices) {
                return {
                  ...curr,
                  choices: matchingConfig.choices,
                  value: getIntegrationParamValue(curr.id),
                  depends: await getConditional(curr),
                };
              } else {
                const val = await getOptions(curr);
                return {
                  ...curr,
                  choices: val,
                  value: getIntegrationParamValue(curr.id),
                  depends: await getConditional(curr),
                };
              }
            } else {
              const data = await getConditional(curr);
              return { ...curr, value: getIntegrationParamValue(curr.id), depends: data };
            }
          }
          return null;
        }),
      );
      const filteredConfigs = configs.filter(config => config !== null);
      setConfigDetails(filteredConfigs);
    };

    fetchData();
  }, [automationLogIntegrationDetails, configDetailValues]);

  const getOptions = curr => {
    return new Promise((resolve, reject) => {
      dispatch(
        getTemplateOptions({
          id: selectedIntegration?.id,
          actionType: selectActions?.action_type,
          paramId: curr?.id,
        }),
      )
        .then(response => {
          resolve(response?.data);
        })
        .catch(err => {
          console.log(err);
          reject(err);
        });
    });
  };

  useEffect(() => {
    sendDataToIntegration(configDetailValues);
  }, [configDetailValues]);

  return (configDetails || []).length ? (
    <div className="grid-trigger w-full">
      {configDetails.map((config, index) => (
        <RenderConditionalConfigs
          key={index}
          config={config}
          updateIntegrationParams={updateIntegrationParams}
          errorFields={errorFields}
        />
      ))}
    </div>
  ) : (
    <div className="flex items-center justify-center w-full">
      <p className="regular-text font-16 grey-text pxy-2">{t('NO_CONFIG_PARAMS_FOUND')}</p>
    </div>
  );
};

export default AutomationLogIntegrationDetails;
